import {
    IInvokerStrategy, ITask, TInvokerStrategySettle, TTaskLimits,
} from './TInvoker';
import TaskProcessor from './tasks/TaskProcessor';
import ParallelIterator from './tasks/ParallelIterator';
import PromiseResolvers from './PromiseResolvers';
import ExecutorFactory from './executor/ExecutorFactory';
import InvokerStrategies from './strategy/InvokerStrategies';

export default class Parallel {
    private readonly promise = new PromiseResolvers<any[]>();

    private readonly iterator: ParallelIterator;

    private readonly processor: TaskProcessor;

    private args: any[] = [];

    constructor(
        tasks: ReadonlyArray<ITask>,
        private readonly limits?: TTaskLimits,
        private readonly strategy: IInvokerStrategy = InvokerStrategies.all,
    ) {
        this.processor = new TaskProcessor(tasks);
        this.iterator = new ParallelIterator(tasks, this.limits?.concurrent);
    }

    execute(...args: any[]): Promise<any[]> {
        this.args = args;

        return this.promise.create(
            () => this.runNextTask(),
        );
    }

    private runNextTask(task?: ITask): void {
        const strategyRes = task && this.strategy(task);
        const runNext = strategyRes?.runNext ?? true;

        while (runNext && this.iterator.next()) {
            this.executeTask();
        }

        if (!runNext) {
            this.processor.cancelTasks();
        }

        if (!runNext || this.iterator.done) {
            this.settle(strategyRes?.settle);
        }
    }

    private async executeTask(): Promise<void> {
        const task = this.iterator.current;
        this.processor.setPending(task);

        const executor = new ExecutorFactory(task, this.limits).create();
        const res = await executor.execute(...this.args);

        this.iterator.complete();
        this.processor.settleTask(task, res);

        this.runNextTask(task);
    }

    private settle(settleTasks?: TInvokerStrategySettle) {
        const result = this.processor.collectResult(settleTasks);

        if (settleTasks === TInvokerStrategySettle.error
            || settleTasks === TInvokerStrategySettle.aggregateError
        ) {
            this.promise.reject(result);
        } else {
            this.promise.resolve(result as any[]);
        }
    }
}
